import { type MaybeRefOrGetter, toValue } from 'vue'
import { useMutationWithInvalidation } from '@/shared/composables/useMutationWithInvalidation'
import { updateYield } from '@/features/assets/api/updateYield'
import { AssetType, YieldAction, YieldFileType } from '@/features/assets/constants'
import { removeYieldFile } from '@/features/assets/api/removeYieldFile'
import { attachYieldFile } from '@/features/assets/api/attachYieldFile'
import { actionYield } from '@/features/assets/api/actionYield'
import type { YieldUpdate } from '@/features/assets/types'
import { createAsset } from '@/features/assets/api/createAsset'
import { acceptAsset } from '@/features/assets/api/acceptAsset'
import { queryKeys } from '@/features/assets/queryKeys'

export const useYieldUpdate = (assetCode: MaybeRefOrGetter<string>) => useMutationWithInvalidation(
  (data: YieldUpdate) => updateYield(toValue(assetCode), data),
  [
    queryKeys.yields.details(assetCode),
    queryKeys.yields.activityLog(assetCode),
  ]
)

export const useYieldFileRemoval = (assetCode: MaybeRefOrGetter<string>) => useMutationWithInvalidation(
  (fileType: YieldFileType) => removeYieldFile(toValue(assetCode), fileType),
  [
    queryKeys.yields.details(assetCode),
    queryKeys.yields.activityLog(assetCode),
  ]
)

export const useYieldFileAttachment = (assetCode: MaybeRefOrGetter<string>) => useMutationWithInvalidation(
  (x: { fileType: YieldFileType, uploadId: string }) => attachYieldFile(toValue(assetCode), x),
  [
    queryKeys.yields.details(assetCode),
    queryKeys.yields.activityLog(assetCode),
  ]
)

export const useYieldAction = (assetCode: MaybeRefOrGetter<string>) => useMutationWithInvalidation(
  (action: YieldAction) => actionYield(toValue(assetCode), action),
  [
    queryKeys.yields.details(assetCode),
    queryKeys.yields.activityLog(assetCode),
  ]
)

export const useCreateAsset = () => useMutationWithInvalidation(
  (input: Parameters<typeof createAsset>[0]) => createAsset(input),
  [
    queryKeys.assets.paginated(),
    input => queryKeys.assets.creationEligibilityLookup({
      assetType: input.type,
      unifiiProjectCode: input.unifiiProjectCode,
      wheelingProjectCode: input.type === AssetType.Wheeling ? input.wheelingProjectCode : undefined,
    })
  ]
)

export const useAcceptAsset = () => useMutationWithInvalidation(
  (assetCode: MaybeRefOrGetter<string>) => acceptAsset(toValue(assetCode)),
  [
    queryKeys.assets.paginated(),
    (assetCode) => queryKeys.assets.details(assetCode),
  ]
)
